import React, { useState, useEffect, ReactNode } from 'react';
import { ProjectsService } from '@/lib/api-generated';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/use-auth-context';
import { ProjectContext, Project } from '@/hooks/use-project-context';
import { logger } from '@/lib/logger';

export const ProjectProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [currentProject, setCurrentProject] = useState<Project | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  // Fetch projects from backend only when authenticated
  useEffect(() => {
    const fetchProjects = async () => {
      // Don't fetch if still loading auth or not authenticated
      if (authLoading || !isAuthenticated) {
        setIsLoading(false);
        return;
      }

      try {
        const data = await ProjectsService.listProjectsApiV1ProjectsGet();
        const projectsWithColor = data.map(project => ({
          ...project,
          color: 'hsl(var(--primary))'
        }));
        setProjects(projectsWithColor as unknown as Project[]);
        
        // Set the first project as current if available
        if (projectsWithColor.length > 0) {
          setCurrentProject(projectsWithColor[0] as unknown as Project);
        }
      } catch (error) {
        logger.error('Failed to fetch projects', error as Error, { context: 'ProjectContext' });
        toast.error('Failed to load projects');
      } finally {
        setIsLoading(false);
      }
    };

    fetchProjects();
  }, [isAuthenticated, authLoading]);

  const handleSetCurrentProject = (project: Project) => {
    const updatedProjects = projects.map(p => 
      p.id === project.id ? { ...p, last_accessed: new Date().toISOString() } : p
    );
    setProjects(updatedProjects);
    setCurrentProject({ ...project, last_accessed: new Date().toISOString() });
  };

  const addProject = (project: Project) => {
    setProjects(prev => [...prev, project]);
    setCurrentProject(project);
  };

  const updateProject = (project: Project) => {
    setProjects(prev => prev.map(p => p.id === project.id ? project : p));
    if (currentProject?.id === project.id) {
      setCurrentProject(project);
    }
  };

  return (
    <ProjectContext.Provider value={{ currentProject, projects, setCurrentProject: handleSetCurrentProject, addProject, updateProject, isLoading }}>
      {children}
    </ProjectContext.Provider>
  );
};

